import { useEffect, useRef, useState } from "react";
import { MessageSquare, X, Send, ArrowUpRight, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { openCalendly } from "@/lib/calendly";

function uid() {
  return "s_" + Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
}

export default function ChatWidget() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  // Boot session + load persisted thread per language
  useEffect(() => {
    const lang = i18n.language || "en";
    const storageKey = `corporaco_chat_${lang}`;
    const sessKey = `corporaco_chat_sess_${lang}`;
    let sid = localStorage.getItem(sessKey);
    if (!sid) {
      sid = uid();
      localStorage.setItem(sessKey, sid);
    }
    setSessionId(sid);
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setMessages(parsed);
        else setMessages([]);
      } else {
        setMessages([]);
      }
    } catch {
      setMessages([]);
    }
  }, [i18n.language]);

  // External trigger — any component can open the widget via:
  //   window.dispatchEvent(new CustomEvent("corpora:open-chat"))
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("corpora:open-chat", handler);
    return () => window.removeEventListener("corpora:open-chat", handler);
  }, []);

  // Persist thread
  useEffect(() => {
    if (!i18n.language) return;
    const storageKey = `corporaco_chat_${i18n.language}`;
    try {
      localStorage.setItem(storageKey, JSON.stringify(messages.slice(-40)));
    } catch {
      /* ignore quota */
    }
  }, [messages, i18n.language]);

  // Auto-scroll on new message / open
  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, sending]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    try {
      const r = await api.post("/chat", {
        session_id: sessionId,
        message: text,
        lang: i18n.language || "en",
      });
      setMessages((m) => [...m, { role: "assistant", content: r.data.reply }]);
    } catch {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: t("chat.errorMessage") },
      ]);
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const suggestions = t("chat.suggestions", { returnObjects: true }) || [];
  const suggestionList = Array.isArray(suggestions) ? suggestions : [];

  return (
    <>
      {/* Floating trigger */}
      <button
        data-testid="chat-trigger"
        onClick={() => setOpen((o) => !o)}
        aria-label="Open chat"
        className="fixed right-5 bottom-5 z-[90] rounded-full flex items-center justify-center transition-all duration-300 hover:scale-105"
        style={{
          width: 54,
          height: 54,
          background: "var(--pewter)",
          color: "var(--ink)",
          boxShadow: "0 12px 40px -8px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.06)",
        }}
      >
        {open ? <X size={22} /> : <MessageSquare size={22} />}
      </button>

      {/* Panel */}
      {open && (
        <div
          data-testid="chat-panel"
          data-lenis-prevent
          className="fixed z-[89] flex flex-col"
          style={{
            bottom: 74,
            right: 16,
            width: "min(380px, calc(100vw - 24px))",
            height: "min(560px, calc(100vh - 100px))",
            maxHeight: "calc(100dvh - 100px)",
            background: "rgba(14,16,20,0.98)",
            backdropFilter: "blur(24px)",
            border: "1px solid var(--line-2)",
            borderRadius: 14,
            overflow: "hidden",
            boxShadow: "0 30px 80px -20px rgba(0,0,0,0.7)",
          }}
        >
          {/* Header */}
          <div
            className="px-5 py-4 flex items-center justify-between"
            style={{ borderBottom: "1px solid var(--line)" }}
          >
            <div className="flex items-center gap-3">
              <div
                className="flex items-center justify-center"
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 8,
                  background: "rgba(190,170,140,0.12)",
                  border: "1px solid var(--line-2)",
                }}
              >
                <Sparkles size={15} style={{ color: "var(--pewter)" }} />
              </div>
              <div>
                <div className="display text-base text-[var(--bone)] leading-tight">
                  {t("chat.title")}
                </div>
                <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[var(--mute-2)]">
                  {t("chat.subtitle")}
                </div>
              </div>
            </div>
            <button
              data-testid="chat-close"
              onClick={() => setOpen(false)}
              className="text-[var(--mute)] hover:text-[var(--bone)]"
              aria-label="Close chat"
            >
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 space-y-3.5"
            data-testid="chat-messages"
            data-lenis-prevent
            style={{ minHeight: 0 }}
          >
            {messages.length === 0 && (
              <div className="space-y-5">
                <div
                  className="px-4 py-3 rounded-lg text-sm text-[var(--bone-2)] leading-relaxed"
                  style={{
                    background: "rgba(255,255,255,0.02)",
                    border: "1px solid var(--line)",
                  }}
                >
                  {t("chat.welcome")}
                </div>
                {suggestionList.length > 0 && (
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--mute-2)] mb-3">
                      {t("chat.tryAsking")}
                    </div>
                    <div className="space-y-2">
                      {suggestionList.map((s, i) => (
                        <button
                          key={i}
                          data-testid={`chat-suggestion-${i}`}
                          onClick={() => {
                            setInput(s);
                          }}
                          className="w-full text-left px-3 py-2.5 rounded-md text-sm text-[var(--bone-2)] hover:text-[var(--bone)] transition-colors"
                          style={{
                            background: "rgba(255,255,255,0.015)",
                            border: "1px solid var(--line)",
                          }}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chat-msg-${m.role}-${i}`}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className="max-w-[85%] px-4 py-3 rounded-lg text-sm leading-relaxed whitespace-pre-wrap"
                  style={
                    m.role === "user"
                      ? {
                          background: "var(--pewter)",
                          color: "var(--ink)",
                        }
                      : {
                          background: "rgba(255,255,255,0.03)",
                          color: "var(--bone-2)",
                          border: "1px solid var(--line)",
                        }
                  }
                >
                  {m.content}
                </div>
              </div>
            ))}

            {sending && (
              <div className="flex justify-start">
                <div
                  className="px-4 py-3 rounded-lg text-sm"
                  style={{
                    background: "rgba(255,255,255,0.03)",
                    color: "var(--mute)",
                    border: "1px solid var(--line)",
                  }}
                  data-testid="chat-typing"
                >
                  <span className="inline-flex gap-1">
                    <span className="typing-dot" />
                    <span className="typing-dot" style={{ animationDelay: "0.15s" }} />
                    <span className="typing-dot" style={{ animationDelay: "0.3s" }} />
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Consultation CTA strip */}
          <button
            type="button"
            data-testid="chat-cta-consult"
            onClick={() => {
              openCalendly();
              setOpen(false);
            }}
            className="px-5 py-3 flex items-center justify-between gap-3 group w-full text-left"
            style={{
              borderTop: "1px solid var(--line)",
              borderBottom: "1px solid var(--line)",
              background: "rgba(190,170,140,0.04)",
            }}
          >
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--bone-2)] group-hover:text-[var(--bone)]">
              {t("chat.bookConsult")}
            </span>
            <ArrowUpRight size={14} style={{ color: "var(--pewter)" }} />
          </button>

          {/* Input */}
          <div className="px-4 py-3 flex items-end gap-2">
            <textarea
              data-testid="chat-input"
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder={t("chat.inputPlaceholder")}
              className="flex-1 resize-none px-3 py-2.5 text-sm text-[var(--bone)] outline-none"
              style={{
                background: "rgba(255,255,255,0.02)",
                border: "1px solid var(--line-2)",
                borderRadius: 8,
                maxHeight: 120,
                fontFamily: "'Inter Tight', sans-serif",
              }}
            />
            <button
              data-testid="chat-send"
              onClick={send}
              disabled={!input.trim() || sending}
              className="flex items-center justify-center disabled:opacity-40 transition-opacity"
              style={{
                width: 40,
                height: 40,
                background: "var(--pewter)",
                color: "var(--ink)",
                borderRadius: 8,
              }}
              aria-label="Send"
            >
              <Send size={15} />
            </button>
          </div>
          <div
            className="px-5 pb-3 font-mono text-[9px] uppercase tracking-[0.2em] text-[var(--mute-2)]"
            data-testid="chat-disclaimer"
          >
            {t("chat.disclaimer")}
          </div>
        </div>
      )}

      <style>{`
        .typing-dot {
          display: inline-block;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--pewter);
          animation: typingDot 1s infinite ease-in-out;
        }
        @keyframes typingDot {
          0%, 60%, 100% { opacity: 0.3; transform: translateY(0); }
          30% { opacity: 1; transform: translateY(-3px); }
        }
      `}</style>
    </>
  );
}
